import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/user'
import { apiClient } from '@/services/api'
import { config } from '@/config'
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const userRole = computed(() => user.value?.role || null)
  const userRoles = computed(() => {
    if (!user.value) return []
    return Array.isArray(user.value.roles) 
      ? user.value.roles 
      : user.value.role 
        ? [user.value.role] 
        : []
  })

  const hasRole = (role: string) => {
    return userRoles.value.includes(role)
  }

  const hasAnyRole = (roles: string[]) => {
    return roles.some(role => userRoles.value.includes(role))
  }

  /**
   * Check if there's an active session
   */
  async function checkSession() {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.get('/auth/me')
      const data = response.data
      
      if (data?.user) {
        user.value = data.user
        return true
      }
      
      // Handle pending onboarding
      if (data?.pendingOnboarding) {
        return false
      }
      
      return false
    } catch (err: any) {
      console.error('Session check error:', err)
      error.value = err.response?.data?.message || err.message || 'Session check failed'
      user.value = null
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Get OAuth login URL
   * For OAuth flow, we redirect to backend which then redirects to IdYangu
   */
  function getLoginUrl(redirectPath?: string): string {
    const redirect = redirectPath || '/'
    return `${config.apiBaseURL}/auth/idyangu/login?redirect=${encodeURIComponent(redirect)}`
  }

  /**
   * Get OAuth registration URL
   * Requires schoolId - redirects to backend which then redirects to IdYangu
   */
  function getRegisterUrl(schoolId: number, redirectPath?: string): string {
    const params = new URLSearchParams({
      school_id: schoolId.toString(),
    })
    if (redirectPath) {
      params.append('redirect', redirectPath)
    }
    return `${config.apiBaseURL}/auth/idyangu/register?${params.toString()}`
  }

  /**
   * Initiate OAuth login (via redirect)
   */
  function initiateLogin(redirectPath?: string) {
    window.location.href = getLoginUrl(redirectPath)
  }

  /**
   * Initiate OAuth registration (via redirect)
   */
  function initiateRegister(schoolId: number, redirectPath?: string) {
    window.location.href = getRegisterUrl(schoolId, redirectPath)
  }

  /**
   * Select school after multi-school login (from OAuth callback)
   */
  async function selectSchool(schoolId: number) {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/auth/select-school', {
        school_id: schoolId
      })
      const data = response.data
      if (data?.user) {
        user.value = data.user
        return { success: true, user: data.user, redirectPath: data.redirectPath }
      }
      return { success: false, message: data?.message || 'School selection failed' }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'School selection failed'
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Switch to a different school (for multi-school users)
   */
  async function switchSchool(schoolId: number) {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/auth/switch-school', {
        school_id: schoolId
      })
      const data = response.data
      if (data?.user) {
        user.value = data.user
        return { success: true, user: data.user }
      }
      return { success: false, message: data?.message || 'School switch failed' }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'School switch failed'
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Add a school via coded signup token (DPA)
   */
  async function addSchool(signupTokenOrSchoolId: string | number) {
    loading.value = true
    error.value = null
    try {
      const body =
        typeof signupTokenOrSchoolId === 'number'
          ? { school_id: signupTokenOrSchoolId }
          : { signup_token: String(signupTokenOrSchoolId) }
      const response = await apiClient.post('/auth/add-school', body)
      const data = response.data
      if (data?.user) {
        user.value = data.user
        return { success: true, user: data.user, school: data.school }
      }
      return { success: false, message: data?.message || 'Failed to add school' }
    } catch (err: any) {
      error.value = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to add school'
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Get pending schools for selection (after OAuth callback)
   */
  async function getPendingSchools() {
    try {
      const response = await apiClient.get('/auth/pending-schools')
      return response.data
    } catch (err: any) {
      console.error('Failed to get pending schools:', err)
      return { requiresSchoolSelection: false, requiresOnboarding: false }
    }
  }

  /**
   * Logout current user
   */
  async function logout() {
    loading.value = true
    try {
      const response = await apiClient.post('/auth/logout')
      // Clear local user state
      user.value = null
      
      // Redirect to IdYangu logout for full SSO logout
      // This clears the IdYangu session so re-login requires authentication
      if (response.data?.idYanguLogoutUrl) {
        window.location.href = response.data.idYanguLogoutUrl
        return // Don't set loading to false since we're redirecting
      }
    } catch (err) {
      console.error('Logout error:', err)
      user.value = null
    } finally {
      loading.value = false
    }
  }

  /**
   * Set error message
   */
  function setError(message: string | null) {
    error.value = message
  }

  /**
   * Clear error message
   */
  function clearError() {
    error.value = null
  }

  // ============================================================
  // Legacy Methods (Deprecated - kept for backward compatibility)
  // ============================================================

  /**
   * @deprecated Use OAuth flow instead (initiateLogin)
   * Direct login with username/password
   */
  async function login(username: string, password: string) {
    console.warn('DEPRECATED: Direct login. Please use OAuth flow.')
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password
      })
      const data = response.data
      
      // Handle school selection requirement for parents with multiple schools
      if (data?.requiresSchoolSelection) {
        return { 
          success: false, 
          requiresSchoolSelection: true,
          schools: data.schools || [],
          message: data.message || 'Please select a school to continue'
        }
      }
      
      if (data?.user) {
        user.value = data.user
        return { success: true, user: data.user }
      }
      return { success: false, message: data?.message || 'Login failed' }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Login failed'
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * @deprecated Use OAuth flow instead (initiateRegister)
   * Direct registration
   */
  async function register(data: {
    username: string
    password: string
    name: string
    phone: string
    role: string
    school_id?: number
  }) {
    console.warn('DEPRECATED: Direct register. Please use OAuth flow.')
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/auth/register', data)
      if (response.data?.user) {
        user.value = response.data.user
        return { success: true, user: response.data.user }
      }
      return { success: false, message: 'Registration failed' }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Registration failed'
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    loading,
    error,
    
    // Computed
    isAuthenticated,
    userRole,
    userRoles,
    
    // Role helpers
    hasRole,
    hasAnyRole,
    
    // Session
    checkSession,
    
    // OAuth Flow (Primary)
    getLoginUrl,
    getRegisterUrl,
    initiateLogin,
    initiateRegister,
    
    // School Management
    selectSchool,
    switchSchool,
    addSchool,
    getPendingSchools,
    
    // Logout
    logout,
    
    // Error handling
    setError,
    clearError,
    
    // Legacy (deprecated)
    login,
    register,
  }
})
